import { useState } from "react";
import { Fingerprint, Check, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  hasLocalKey,
  registerBiometricKey,
  requestBiometricGesture,
  signMessage,
} from "@/lib/crypto";
import { useAuth } from "@/context/AuthContext";

export default function BiometricModal({ open, onClose, message, actionLabel, onComplete }) {
  const { user } = useAuth();
  const [phase, setPhase] = useState("idle"); // idle | working | done
  const [note, setNote] = useState("");

  if (!open) return null;

  const seal = async () => {
    setPhase("working");
    try {
      if (!(await hasLocalKey(user.email))) {
        setNote("Setting up this device to seal…");
        const pub = await registerBiometricKey(user.email);
        await api.post("/keys/register", { public_key_der: pub });
      }
      setNote("Verifying your identity…");
      const used = await requestBiometricGesture(user.email);
      setNote(used ? "Verified. Signing…" : "Signing with your device key…");
      let signature;
      try {
        signature = await signMessage(user.email, message);
      } catch (signErr) {
        // Local key missing/unusable — re-register a fresh device key once and retry.
        console.warn("signing failed, re-registering device key:", signErr?.message || signErr);
        const pub = await registerBiometricKey(user.email);
        await api.post("/keys/register", { public_key_der: pub });
        signature = await signMessage(user.email, message);
      }
      setPhase("done");
      setTimeout(() => onComplete(signature), 600);
    } catch (e) {
      setPhase("idle");
      const detail = e?.response?.data?.detail || e?.message || "";
      console.error("biometric seal failed:", e);
      toast.error(detail ? `Couldn't seal: ${detail}` : "Biometric signing failed. Please try again.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4"
      data-testid="biometric-modal"
    >
      <div className="bg-white rounded-2xl border border-[#DEDBD3] w-full max-w-md p-8 text-center animate-fade-up relative">
        <button
          onClick={onClose}
          data-testid="biometric-close-btn"
          className="absolute top-4 right-4 text-[#A3A095] hover:text-[#1A1A1A]"
        >
          <X size={20} />
        </button>

        <p className="overline text-[#E07A5F] mb-2">Biometric Seal</p>
        <h3 className="font-serif-dl text-3xl tracking-tight">Place your mark</h3>
        <p className="text-[#5C5A54] mt-2 text-sm leading-relaxed">
          Your biometric acts as your signature on this dap. This binds your word
          to the ledger.
        </p>

        <div className="my-10 flex items-center justify-center">
          <div
            className={`relative w-28 h-28 rounded-full flex items-center justify-center transition-colors ${
              phase === "done" ? "bg-[#E6F0EB]" : "bg-[#F0EBE1]"
            } ${phase === "working" ? "biometric-ring" : ""}`}
          >
            {phase === "done" ? (
              <Check className="text-[#2E6B4F]" size={44} strokeWidth={2} />
            ) : (
              <Fingerprint className="text-[#1B4D3E]" size={48} strokeWidth={1.2} />
            )}
          </div>
        </div>

        {phase === "done" ? (
          <p className="font-serif-dl text-2xl text-[#2E6B4F]">Sealed.</p>
        ) : (
          <>
            <button
              data-testid="biometric-confirm-btn"
              disabled={phase === "working"}
              onClick={seal}
              className="w-full py-4 bg-[#E07A5F] hover:bg-[#C96A51] disabled:opacity-60 text-white rounded-lg flex items-center justify-center gap-2 font-medium tracking-wide"
            >
              <Fingerprint size={18} />
              {phase === "working" ? "Working…" : actionLabel || "Seal with Biometrics"}
            </button>
            <p className="text-xs text-[#A3A095] mt-3 h-4">{note}</p>
          </>
        )}
      </div>
    </div>
  );
}
